<script lang="ts">
  import "./app.css";
  import axios from "axios";
  import {
    BASE_URL,
    disableCommunicationRedirects,
    disableWidget,
    languageConfig,
    urlChangeListenerClients,
    WIDGET_BOTTOM_SPACE,
  } from "./utils/constants";
  import { resolveButtonSizes } from "./utils/buttonSizing";
  import { onMount, afterUpdate, onDestroy } from "svelte";
  import { fly } from "svelte/transition";
  import {
    configLoaded,
    isOpen,
    openWidget,
    isConfigLoaded,
    hasOpened,
  } from "./stores/widget";
  import {
    client_id,
    messages,
    sendMessage,
    showThinking,
    setBotPresetMessageString,
    activeAgentIndex,
    activeAssistantIndex,
    activeTyping,
    setBotPresetMessage,
    extractCityAndPropertyName,
    displayMessage,
    showInput,
    toggleShowInput,
    user_details_form_submitted,
    pendingMessages,
    input_field_enabled,
    selected_communication,
    resetConnection,
    is_feedback_form_enabled,
    setUserEmail,
    setBotVersion,
    clearSendMessageInputValue,
    user_email,
    is_logged_in,
    selected_language,
    setLanguage,
    getCurrentLanguage,
    detectBot,
    is_bot_detected,
    setBotPresetMessageForRoomType,
    property_name,
    city_name,
    loadUtmDetails,
    handleStartChatting,
    is_privacy_policy_accepted,
    getLanguageCodeFromUrl,
    findLanguageName,
    attachmentModalOpen,
    getDynamicPropertyMessage,
    setClientAiEnabled,
  } from "./stores/chat";
  import { AssetLoader } from "./utils/assetLoader";
  import { trackClick } from "./utils/clickTracker";
  import UserDetailsForm from "./components/UserDetailsForm.svelte";
  import { sanitizeHtml } from "./utils/sanitize";
  import EmailIcon from "./utils/assets/email_icon.svelte";
  import PhoneIcon from "./utils/assets/phone_icon.svelte";
  import WhatsappIcon from "./utils/assets/whatsapp_icon.svelte";
  import SmsIcon from "./utils/assets/sms_icon.svelte";
  import ThinkingIndicator from "./components/ThinkingIndicator.svelte";
  import CommunicationRedirects from "./components/CommunicationRedirects.svelte";
  import ChatWidgetFooter from "./components/ChatWidgetFooter.svelte";
  import ChatWidgetLauncher from "./components/ChatWidgetLauncher.svelte";
  import ChatWidgetHeader from "./components/ChatWidgetHeader.svelte";
  import CommunicationModalities from "./components/CommunicationModalities.svelte";
  import WechatQrCard from "./components/WechatQrCard.svelte";
  import ChatMessage from "./components/ChatMessage.svelte";
  import FeedbackForm from "./components/FeedbackForm.svelte";
  import type {
    FeedbackFormQuestion,
    FeedbackFormTexts,
    ModalityConfig,
  } from "./types/globalTypes";
  import { client_config } from "./stores/store";
  import AttachmentModal from "./components/AttachmentModal.svelte";
  import { scopedLocalStorage, scopedSessionStorage } from "./utils/storage";
  import FbIcon from "./utils/assets/fb_icon.svelte";
  import InstagramIcon from "./utils/assets/instagram_icon.svelte";

  let formData: { [key: string]: string } = {};

  function hasUserFormData() {
    try {
      const storedData = scopedLocalStorage.getItem("user_form_details");
      if (!storedData) return false;

      const parsedData = JSON.parse(storedData);
      return (
        Object.keys(parsedData).length > 0 &&
        Object.values(parsedData).some((value) => {
          return value && typeof value === "string" && value.trim() !== "";
        })
      );
    } catch (error) {
      return false;
    }
  }

  const handleCommunicationClick = async (
    type: "email" | "call" | "whatsapp" | "sms" | "fb_messenger" | "instagram",
  ) => {
    if (type === "email") {
      window.location.href = $client_config?.email_link;
    } else if (type === "call") {
      window.location.href = `tel:${$client_config?.call_link?.replace(/[^\d+]/g, "")}`;
    } else if (type === "whatsapp") {
      window.open($client_config?.whatsapp_link, "_blank");
    } else if (type === "sms") {
      window.location.href = $client_config?.sms_link;
    } else if (type === "fb_messenger") {
      window.open($client_config?.fb_messenger_link, "_blank");
    } else if (type === "instagram") {
      window.open($client_config?.instagram_link, "_blank");
    }

    try {
      await trackClick(id, type, "widget");
    } catch (error) {
      console.error("Failed to track click:", error);
    }
  };

  export let id: string;
  export let email: string | null = null;
  export let bot_version: string | null = null;
  export let display: {
    forceFullscreen: boolean;
    hideControls: {
      back: boolean;
      toggle: boolean;
      communicationRedirects: boolean;
    };
  } = {
    forceFullscreen: false,
    hideControls: {
      back: false,
      toggle: false,
      communicationRedirects: false,
    },
  };
  export let accentColor = "#000000";
  export let feedback_form_config: FeedbackFormTexts = {
    headers: {
      header: "",
      sub_header: "",
      feedback_submit_button: "",
      feedback_skip_button: "",
      submission_header: "",
      submission_sub_header: "",
      submit_button: "",
      skip_button: "",
    },
    questions: [],
  };

  interface ChatState {
    currentDashboardUrl: string;
    chatContainer: HTMLDivElement | null;
    previousMessageCount: number;
    modalityConfig: ModalityConfig;
  }

  const initialState: ChatState = {
    currentDashboardUrl: "",
    chatContainer: null,
    previousMessageCount: $messages.length + $pendingMessages.length,
    modalityConfig: {
      chat_enabled: false,
      call: {
        enabled: false,
        cta: "",
        message: "",
      },
      mail: {
        enabled: false,
        cta: "",
        message: "",
      },
      whatsapp: {
        enabled: false,
        cta: "",
        message: "",
      },
      sms: {
        enabled: false,
        cta: "",
        message: "",
      },
      fb_messenger: {
        enabled: false,
        cta: "",
        message: "",
      },
      instagram: {
        enabled: false,
        cta: "",
        message: "",
      },
    },
  };

  let {
    currentDashboardUrl,
    chatContainer,
    previousMessageCount,
    modalityConfig,
  } = initialState;

  let isUserMessage = false;
  let previous_language: string | null = null;
  let should_show_communication_redirects = true;
  let should_show_widget = true;
  let isVerbafloLeadId: string | null = null;

  if (!id) {
    throw new Error("Please provide a valid id");
  } else {
    $client_id = id;
    if (email) {
      setUserEmail(email);
    }
    if (bot_version) {
      setBotVersion(bot_version);
    }
  }

  let autoOpenTimeout: ReturnType<typeof setTimeout> | null = null;

  const afterUpdateLanguageChange = () => {
    feedback_form_config = {
      headers: $client_config?.questionnaire_config?.headers,
      questions:
        $client_config?.questionnaire_config?.questions?.length > 0
          ? $client_config?.questionnaire_config?.questions
              ?.map((question: FeedbackFormQuestion) => ({
                ...question,
                answer: "",
              }))
              ?.slice(0, 2)
          : [],
    };

    if ($messages?.length === 1 && $client_config?.welcome_messages) {
      const is_logged_in_user = $user_email && $is_logged_in;

      let updated_initial_messages = [...$messages];

      updated_initial_messages[0] = {
        ...updated_initial_messages[0],
        message: is_logged_in_user
          ? "Welcome back! How can I help you today?"
          : $client_config?.welcome_messages?.original_message ||
            updated_initial_messages[0].message,
        translated_message: is_logged_in_user
          ? ""
          : $client_config?.welcome_messages?.translated_message ||
            updated_initial_messages[0].translated_message,
      };

      messages?.set(updated_initial_messages);
    }
  };

  onMount(() => {
    if (id === "studentcastle") {
      document.getElementById("vfwidget")?.classList.add(
        "verbaflo-client-studentcastle",
      );
    }
    detectBot();
    loadFonts();
    loadUtmDetails();
    init();
    if (urlChangeListenerClients.includes(id)) {
      setupUrlChangeListeners();
    }

    setTimeout(() => {
      const widgetContainer = document.getElementById("vfwidget");
      if (widgetContainer) {
        widgetContainer.setAttribute("data-lenis-prevent", "true");

        // Prevent wheel and scroll events from reaching Lenis
        const handleWheel = (e: Event) => {
          e.stopPropagation();
          e.stopImmediatePropagation();
        };

        const handleScroll = (e: Event) => {
          e.stopPropagation();
          e.stopImmediatePropagation();
        };

        // Add event listeners to prevent Lenis from handling wheel and scroll events only
        widgetContainer.addEventListener("wheel", handleWheel, {
          capture: true,
          passive: false,
        });
        widgetContainer.addEventListener("scroll", handleScroll, {
          capture: true,
          passive: false,
        });
      }
    }, 100);

    return () => {
      if (autoOpenTimeout) {
        clearTimeout(autoOpenTimeout);
        autoOpenTimeout = null;
      }
    };
  });

  onDestroy(() => {
    is_bot_detected?.set(false);
  });

  afterUpdate(async () => {
    if (
      $selected_language?.code &&
      $selected_language?.code !== previous_language
    ) {
      previous_language = $selected_language?.code;

      const config_language_url = bot_version
        ? `${BASE_URL}/fe/get_client_config/${id}?language=${$selected_language?.code}&bot_version=${bot_version}`
        : `${BASE_URL}/fe/get_client_config/${id}?language=${$selected_language?.code}`;

      const response = await axios.get(config_language_url);

      client_config?.set(response?.data);

      afterUpdateLanguageChange();
    }
  });

  const shouldDisableCommunicationRedirects = (
    url: string,
    clientId: string,
  ) => {
    if (!disableCommunicationRedirects.clients.includes(clientId)) {
      return false;
    }
    const disabledUrls = (disableCommunicationRedirects as any)[clientId] || [];
    return disabledUrls.some((disabledUrl: string) =>
      url.includes(disabledUrl.replace(/^https?:\/\/[^\/]+/, "")),
    );
  };

  const shouldDisableWidget = (url: string, clientId: string) => {
    if (!disableWidget.clients.includes(clientId)) {
      return false;
    }
    const disabledUrls = ((disableWidget as any)[clientId] as string[]) || [];
    return disabledUrls.some((disabledUrl: string) =>
      url.includes(disabledUrl.replace(/^https?:\/\/[^\/]+/, "")),
    );
  };

  // initial load
  const init = async () => {
    const assetLoader = AssetLoader?.getInstance();

    const languageCodeFromUrl = getLanguageCodeFromUrl(id, languageConfig);
    const languageCodeFromStorage = getCurrentLanguage(id, languageConfig);

    let current_language_code = languageCodeFromUrl
      ? languageCodeFromUrl
      : languageCodeFromStorage;

    if (languageCodeFromUrl) {
      const current_language_name = findLanguageName(
        languageCodeFromUrl,
        languageConfig,
      );

      if (current_language_name) {
        current_language_code = languageCodeFromUrl;
      }
    }

    try {
      const config_url = bot_version
        ? `${BASE_URL}/fe/get_client_config/${id}?language=${current_language_code}&bot_version=${bot_version}`
        : `${BASE_URL}/fe/get_client_config/${id}?language=${current_language_code}`;

      const response = await axios?.get(config_url);

      client_config?.set(response?.data);

      if (current_language_code) {
        const current_language_name = findLanguageName(
          current_language_code,
          languageConfig,
        );

        if (current_language_name?.length > 0) {
          setLanguage({
            language: current_language_code,
            name: current_language_name,
          });
          previous_language = current_language_code;
        } else {
          const current_language_code =
            getCurrentLanguage(id, languageConfig) ?? "en";

          const current_language_name = findLanguageName(
            current_language_code,
            languageConfig,
          );

          if (current_language_name) {
            setLanguage({
              language: current_language_code,
              name: current_language_name,
            });
            previous_language = current_language_code;
          }
        }

        afterUpdateLanguageChange();
      }

      accentColor = $client_config?.accent_color ?? accentColor;
      modalityConfig = $client_config?.modality_config;
      setClientAiEnabled($client_config?.modality_config?.chat_enabled ?? true);
      feedback_form_config = {
        headers: $client_config?.questionnaire_config?.headers,
        questions:
          $client_config?.questionnaire_config?.questions?.length > 0
            ? $client_config?.questionnaire_config?.questions
                ?.map((question: FeedbackFormQuestion) => ({
                  ...question,
                  answer: "",
                }))
                ?.slice(0, 2)
            : [],
      };
      is_feedback_form_enabled?.set(
        $client_config?.questionnaire_config?.enabled,
      );
      input_field_enabled.set($client_config?.chat_box_input?.is_enabled);

      if ($client_config?.form_fields?.enabled) {
        user_details_form_submitted.set(
          scopedLocalStorage.getItem("user_details_form_submitted")
            ? true
            : false,
        );
      } else {
        user_details_form_submitted.set(true);
      }

      if (scopedSessionStorage.getItem("chatbot_opened")) {
        hasOpened.set(
          scopedSessionStorage.getItem("chatbot_opened") === "true",
        );
      }

      if (!currentDashboardUrl) {
        currentDashboardUrl = window.location.href;

        const parsedUrl = new URL(currentDashboardUrl);
        isVerbafloLeadId =
          parsedUrl?.searchParams.get("verbaflo_lead_id") || null;

        if (!isVerbafloLeadId) {
          handleUrlChange(currentDashboardUrl);
        }
      }

      if ($client_config?.welcome_messages) {
        if ($user_email && $is_logged_in) {
          setBotPresetMessageString({
            original_message: "Welcome back! How can I help you today?",
            translated_message: "",
          });
        } else {
          if (isVerbafloLeadId) {
            try {
              const response = await axios.get(
                `${BASE_URL}/cross_client_conversation_summary/${id}/${isVerbafloLeadId}`,
              );
              const data = await response.data;
              setBotPresetMessageString({
                original_message: data.response
                  ? String(data.response)
                  : $client_config?.welcome_messages.original_message,
                translated_message: "",
              });
            } catch (error) {
              setBotPresetMessageString($client_config?.welcome_messages);
            }
          } else {
            setBotPresetMessageString($client_config?.welcome_messages);
          }
        }
      }

      if ($client_config?.selected_alert_sound) {
        const audioUrl = new URL($client_config?.selected_alert_sound).href;
        const notificationSound = new Audio(audioUrl);
        notificationSound.load();
        assetLoader.setAsset("notification", notificationSound);
      }

      if (disableCommunicationRedirects.clients.includes(id)) {
        const currentUrl = window?.location?.href;
        should_show_communication_redirects =
          !shouldDisableCommunicationRedirects(currentUrl, id);
      }

      if (disableWidget.clients.includes(id)) {
        const currentUrl = window?.location?.href;
        should_show_widget = !shouldDisableWidget(currentUrl, id);
      }

      configLoaded();

      if (isConfigLoaded.get()) {
        const delayBot = ($client_config?.open_delay ?? 0) * 1000;
        let phoneDelayBot =
          $client_config?.phone_delay >= 0
            ? ($client_config?.phone_delay ?? 0) * 1000
            : $client_config?.phone_delay;

        if (window.innerWidth < 700) {
          if (
            phoneDelayBot >= 0 &&
            !scopedSessionStorage.getItem("chatbot_opened")
          ) {
            autoOpenTimeout = setTimeout(() => {
              if (!scopedSessionStorage.getItem("chatbot_opened")) {
                openWidget();
                scopedSessionStorage.setItem("chatbot_opened", "true");
              }
            }, phoneDelayBot);
          }
        } else {
          if (
            delayBot >= 0 &&
            !scopedSessionStorage.getItem("chatbot_opened")
          ) {
            autoOpenTimeout = setTimeout(() => {
              if (!scopedSessionStorage.getItem("chatbot_opened")) {
                openWidget();
                scopedSessionStorage.setItem("chatbot_opened", "true");
              }
            }, delayBot);
          }
        }

        setBotPresetMessage(currentDashboardUrl);
        setTimeout(() => {
          setBotPresetMessageForRoomType();
        }, 5000);
      }
    } catch (error) {
      console.error("Failed to load config:", error);
    }
  };

  const handleUrlChange = (url: string) => {
    const parsedUrl = new URL(url);
    isVerbafloLeadId = parsedUrl?.searchParams?.get("verbaflo_lead_id") || null;
    extractCityAndPropertyName(url);

    if (disableCommunicationRedirects.clients.includes(id)) {
      should_show_communication_redirects =
        !shouldDisableCommunicationRedirects(url, id);
    }

    if (disableWidget.clients.includes(id)) {
      should_show_widget = !shouldDisableWidget(url, id);
    }
  };

  const setupUrlChangeListeners = () => {
    // Listen for popstate events (browser back/forward)
    window.addEventListener("popstate", () => {
      extractCityAndPropertyName(window.location.href);
      updateCityAndPropertyNamePresetMessage();
    });

    // Listen for programmatic URL changes
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(this, args);
      extractCityAndPropertyName(window.location.href);
      updateCityAndPropertyNamePresetMessage();
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      extractCityAndPropertyName(window.location.href);
      updateCityAndPropertyNamePresetMessage();
    };
  };

  const updateCityAndPropertyNamePresetMessage = () => {
    const hasOnlyAssistantMessage = $messages.every(
      (msg) => msg?.role === "assistant",
    );

    const dynamicMessage = getDynamicPropertyMessage();

    if (
      urlChangeListenerClients?.includes(id) &&
      $messages?.length === 2 &&
      hasOnlyAssistantMessage &&
      (dynamicMessage || city_name.get() || property_name.get())
    ) {
      if (dynamicMessage) {
        const newMessages = [...$messages];
        newMessages[1] = {
          ...newMessages[1],
          message: dynamicMessage,
        };
        messages.set(newMessages);
        return;
      }

      const cityName = city_name.get();
      const propertyName = property_name.get();

      if (cityName && propertyName) {
        const newMessages = [...$messages];
        newMessages[1] = {
          ...newMessages[1],
          message: `I can see you're looking at **${propertyName}** in **${cityName}**. Are you just browsing or do you need any help?`,
        };
        messages.set(newMessages);
      } else if (cityName) {
        let message = "";
        if (
          (cityName === "Milan" ||
            cityName === "Gdańsk" ||
            cityName === "Kraków") &&
          client_id.get() === "milestone"
        ) {
          message = `I see you're exploring property at **${cityName}**. Are you just browsing or is there anything I can help you with?`;
        } else {
          message = `I see you're exploring properties in **${cityName}**. Are you just browsing or is there anything I can help you with?`;
        }
        const newMessages = [...$messages];
        newMessages[1] = {
          ...newMessages[1],
          message: message,
        };

        messages.set(newMessages);
      }
    } else if (
      urlChangeListenerClients?.includes(id) &&
      $messages?.length === 1 &&
      hasOnlyAssistantMessage &&
      (dynamicMessage || city_name.get() || property_name.get())
    ) {
      if (dynamicMessage) {
        const newMessages = [...$messages];
        newMessages.push({
          message: dynamicMessage,
          role: "assistant",
          timestamp: new Date(Date.now() + 5000).toISOString(),
          property_json: null,
          language: scopedLocalStorage.getItem("lang") || "en",
        });
        messages.set(newMessages);
        return;
      }

      const newMessages = [...$messages];

      const cityName = city_name.get();
      const propertyName = property_name.get();

      if (propertyName && cityName) {
        newMessages.push({
          message: `I can see you're looking at **${propertyName}** in **${cityName}**. Are you just browsing or do you need any help?`,
          role: "assistant",
          timestamp: new Date(Date.now() + 5000).toISOString(),
          property_json: null,
          language: scopedLocalStorage.getItem("lang") || "en",
        });
      } else if (cityName) {
        let message = "";
        if (
          (cityName === "Milan" ||
            cityName === "Gdańsk" ||
            cityName === "Kraków") &&
          client_id.get() === "milestone"
        ) {
          message = `I see you're exploring property at **${cityName}**. Are you just browsing or is there anything I can help you with?`;
        } else {
          message = `I see you're exploring properties in **${cityName}**. Are you just browsing or is there anything I can help you with?`;
        }
        newMessages.push({
          message: message,
          role: "assistant",
          timestamp: new Date(Date.now() + 5000).toISOString(),
          property_json: null,
          language: scopedLocalStorage.getItem("lang") || "en",
        });
      }

      messages.set(newMessages);
    } else if (
      urlChangeListenerClients?.includes(id) &&
      $messages?.length === 2 &&
      hasOnlyAssistantMessage &&
      (!dynamicMessage && (!city_name.get() || !property_name.get()))
    ) {
      messages.set([$messages[0]]);
    }
  };

  const sendBatchedMessages = () => {
    showThinking.set(true);
    sendMessage();
  };

  const parseMessage = (message: string) => {
    try {
      const data = JSON.parse(
        scopedLocalStorage.getItem("user_form_details") || "{}",
      );
      if (
        $client_config?.form_fields?.fields?.length > 0 &&
        Object?.keys(data)?.length > 0
      ) {
        $client_config?.form_fields?.fields?.forEach(
          (field: { code: string }) => {
            if (data[field.code]) {
              const value = data[field.code]
                .trim()
                .split(/\s+/)
                ?.map((word: string) => word[0].toUpperCase() + word.slice(1))
                .join(" ");
              message = message?.replace(`{{${field.code}}}`, value);
            }
          },
        );
      }
    } catch (error) {}
    return message;
  };

  $: if ($isOpen && chatContainer) {
    const lastMessage = $messages[$messages?.length - 1];
    const lastMessageIndex = $messages?.length - 1;

    if (
      lastMessage?.property_json &&
      Array.isArray(lastMessage?.property_json) &&
      lastMessage?.property_json?.length > 0
    ) {
      const propertyElement = chatContainer?.querySelector(
        `[data-property-index="${lastMessageIndex}"]`,
      );
      if (propertyElement) {
        propertyElement?.scrollIntoView();
      }
    } else {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }

  afterUpdate(() => {
    if (chatContainer) {
      const currentMessageCount = $messages.length + $pendingMessages.length;

      if (currentMessageCount > previousMessageCount) {
        const lastMessage = $messages[$messages.length - 1];
        isUserMessage = lastMessage?.role === "user";

        if (isUserMessage) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        } else {
          const lastMessage = $messages[$messages?.length - 1];
          const lastMessageIndex = $messages?.length - 1;

          if (
            lastMessage?.property_json &&
            Array.isArray(lastMessage?.property_json) &&
            lastMessage.property_json.length > 0
          ) {
            const propertyElement = chatContainer.querySelector(
              `[data-property-index="${lastMessageIndex}"]`,
            );
            if (propertyElement) {
              propertyElement?.scrollIntoView();
            }
          } else {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }
      }

      previousMessageCount = currentMessageCount;
    }
    if ($messages?.length > 0) {
      const newMessage = [...$messages];
      newMessage?.forEach((message) => {
        if (message.role === "assistant") {
          message.message = parseMessage(message.message);
        }
      });
      messages.set(newMessage);
    }
    $input_field_enabled
      ? showInput.set(true)
      : showInput.set(toggleShowInput([...$messages]));
  });

  function loadFonts() {
    const assetLoader = AssetLoader.getInstance();
    const CDN_URL = assetLoader.getCDNUrl();
    const preloadFonts = [
      `${CDN_URL}/fonts/Satoshi-Variable.woff2`,
      `${CDN_URL}/fonts/Satoshi-VariableItalic.woff2`,
      `${CDN_URL}/fonts/Unbounded-Variable.woff2`,
    ];

    preloadFonts.forEach((fontUrl) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.href = fontUrl;
      link.as = "font";
      link.type = "font/woff2";
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    });

    const style = document.createElement("style");
    style.textContent = `
      @font-face {
        font-family: "Satoshi";
        src: url("${CDN_URL}/fonts/Satoshi-Variable.woff2") format("woff2");
        font-weight: 300 900;
        font-style: normal;
        font-display: swap;
      }
      @font-face {
        font-family: "Satoshi-VariableItalic";
        src: url("${CDN_URL}/fonts/Satoshi-VariableItalic.woff2") format("woff2");
        font-weight: 300 900;
        font-style: italic;
        font-display: swap;
      }
      @font-face {
        font-family: "Unbounded";
        src: url("${CDN_URL}/fonts/Unbounded-Variable.woff2") format("woff2");
        font-weight: 300 900;
        font-style: normal;
        font-display: swap;
      }
      #vfwidget,
      #vfwidget * {
        font-family: "Satoshi", sans-serif !important;
      }
    `;
    document.head.appendChild(style);
  }

  const startNewChat = async () => {
    // Clear form data first when starting new chat
    formData = {};
    await resetConnection($client_config);
    if ($client_config?.welcome_messages) {
      if ($user_email && $is_logged_in) {
        setBotPresetMessageString({
          original_message: "Welcome back! How can I help you today?",
          translated_message: "",
        });
      } else {
        setBotPresetMessageString($client_config?.welcome_messages);
      }
    }
    isVerbafloLeadId = null;
    setBotPresetMessage(currentDashboardUrl);
    setBotPresetMessageForRoomType();
    clearSendMessageInputValue();
    feedback_form_config =
      feedback_form_config?.questions?.length > 0
        ? {
            ...feedback_form_config,
            questions: feedback_form_config?.questions
              ?.map((question) => ({
                ...question,
                answer: "",
              }))
              ?.slice(0, 2),
          }
        : feedback_form_config;
  };

  $: hasEnabledModalities =
    (modalityConfig?.call?.enabled && $client_config?.call_link) ||
    (modalityConfig?.whatsapp?.enabled && $client_config?.whatsapp_link) ||
    (modalityConfig?.mail?.enabled && $client_config?.email_link) ||
    (modalityConfig?.sms?.enabled && $client_config?.sms_link) ||
    (modalityConfig?.fb_messenger?.enabled &&
      $client_config?.fb_messenger_link) ||
    (modalityConfig?.instagram?.enabled && $client_config?.instagram_link) ||
    (modalityConfig?.wechat?.enabled && $client_config?.wechat_link);

  $: privacyPolicyBottom = hasEnabledModalities ? "44px" : "14px";
  $: widgetPosition =
    $client_config?.widget_positioning?.position || "bottom_right";
  $: isLeftPositioned = widgetPosition === "bottom_left";
  $: horizontalOffset =
    ($client_config?.widget_positioning?.horizontal_offset_px ?? 16) + "px";
  $: verticalOffsetPx =
    $client_config?.widget_positioning?.vertical_offset_px ?? 16;
  $: verticalOffset = verticalOffsetPx + "px";

  // Keep the open chat window sitting directly above the launcher, so the gap
  // stays fixed regardless of the (configurable) launcher size. Defaults to 80
  // (the previous fixed launcher height) when no size is configured.
  $: launcherHeightDesktopPx =
    parseInt(resolveButtonSizes($client_config).launcher.desktop || "80", 10) ||
    80;

  $: widgetParentContainerBottomSpace =
    typeof window !== "undefined" && window.innerWidth < 768
      ? "0px"
      : verticalOffsetPx + launcherHeightDesktopPx + "px";
</script>

{#if should_show_widget}
  <div
    class="verbaflo-widget-container"
    class:verbaflo-widget-position-left={isLeftPositioned}
    style="--vf-h-offset: {horizontalOffset};"
  >
    {#if $isConfigLoaded && ((disableCommunicationRedirects.clients.includes(id) && should_show_communication_redirects) || (!disableCommunicationRedirects.clients.includes(id) && !(display?.hideControls?.communicationRedirects ?? false)))}
      <CommunicationRedirects {id} {verticalOffset} />
    {/if}
    {#if $isConfigLoaded && !(display?.hideControls?.toggle ?? false)}
      <ChatWidgetLauncher {isLeftPositioned} {verticalOffset} />
    {/if}
    {#if $isConfigLoaded && ($isOpen || (display?.forceFullscreen ?? false))}
      <div
        in:fly={{ y: 50, duration: 300 }}
        out:fly={{ y: 50, duration: 100 }}
        class="verbaflo-widget-parent-container"
        style="bottom:{widgetParentContainerBottomSpace} !important;"
      >
        <ChatWidgetHeader
          {accentColor}
          showBackButton={!(display?.hideControls?.back ?? false)}
          onStartNewChat={startNewChat}
        />
        <div class="verbaflo-widget-chat-container-wrapper">
          <div
            bind:this={chatContainer}
            class="verbaflo-widget-chat-container scrollbar-hide"
          >
            <div>
              {#if $selected_communication === "phone" || $selected_communication === "whatsapp" || $selected_communication === "email" || $selected_communication === "sms" || $selected_communication === "fb_messenger" || $selected_communication === "instagram" || $selected_communication === "wechat"}
                <div class="verbaflo-widget-message-wrapper">
                  <img
                    src={$client_config?.client_logo_url ||
                      "https://vf-assets.verbaflo.com/logo.png"}
                    alt="Bot Profile"
                    class="verbaflo-widget-profile-image"
                  />
                  <div class="verbaflo-widget-message-bubble">
                    <p class="verbaflo-widget-message-sender-name">
                      {$client_config?.bot_name || "Verbaflo"}
                    </p>
                    <div class="verbaflo-widget-message-content-container">
                      {#if $selected_communication === "phone"}
                        <p>
                          {$client_config?.modality_config?.call?.message ||
                            "Prefer to speak over the phone? Tap below to call us."}
                        </p>
                      {/if}
                      {#if $selected_communication === "whatsapp"}
                        <p>
                          {$client_config?.modality_config?.whatsapp?.message ||
                            "Want to chat on WhatsApp? Tap below to start the conversation."}
                        </p>
                      {/if}
                      {#if $selected_communication === "email"}
                        <p>
                          {$client_config?.modality_config?.mail?.message ||
                            "Got questions? Send us an email and we'll reply soon!"}
                        </p>
                      {/if}
                      {#if $selected_communication === "sms"}
                        <p>
                          {$client_config?.modality_config?.sms?.message ||
                            "Want to text us? Tap below to send a message."}
                        </p>
                      {/if}
                      {#if $selected_communication === "fb_messenger"}
                        <p>
                          {$client_config?.modality_config?.fb_messenger
                            ?.message ||
                            "Want to chat on Facebook Messenger? Tap below to start the conversation."}
                        </p>
                      {/if}
                      {#if $selected_communication === "instagram"}
                        <p>
                          {$client_config?.modality_config?.instagram
                            ?.message ||
                            "Want to chat on Instagram? Tap below to start the conversation."}
                        </p>
                      {/if}
                      {#if $selected_communication === "wechat"}
                        <p>
                          {$client_config?.modality_config?.wechat?.message ||
                            "Want to chat on WeChat? Scan the code and we'll bring this chat with us."}
                        </p>
                      {/if}
                    </div>
                  </div>
                </div>
                {#if $selected_communication === "wechat"}
                  <div class="verbaflo-widget-wechat-inline">
                    <WechatQrCard
                      qrUrl={$client_config?.wechat_link}
                      title="Scan to continue on WeChat"
                    />
                  </div>
                {/if}
              {/if}
            </div>
            {#if $client_config?.form_fields && $client_config?.form_fields?.enabled && !$user_details_form_submitted && $selected_communication === "message" && !hasUserFormData()}
              <UserDetailsForm
                formFields={$client_config?.form_fields?.fields}
                title={$client_config?.form_fields?.title}
                {formData}
                {accentColor}
              />
            {:else}
              <div class="verbaflo-widget-chat-message-container">
                {#if $selected_communication === "message"}
                  {#each $messages as message, index}
                    <ChatMessage
                      {message}
                      {index}
                      isLastMessage={index === $messages.length - 1}
                      {accentColor}
                      activeAgentIndex={[...$activeAgentIndex]}
                      activeAssistantIndex={[...$activeAssistantIndex]}
                      onQuickReplyClick={(text) => {
                        displayMessage(text, "quickreply");
                        sendBatchedMessages();
                      }}
                      {id}
                    />
                  {/each}
                {/if}
                {#if $showThinking && $selected_communication === "message" && ($activeTyping ?? true) && $client_config?.widget_enabled}
                  <ThinkingIndicator />
                {/if}
              </div>
            {/if}
          </div>

          <ChatWidgetFooter {accentColor} {isVerbafloLeadId} />

          {#if !$is_privacy_policy_accepted && $selected_communication === "message" && $messages?.every((msg) => msg?.role === "assistant") && $client_config?.consent_capture && !$client_config?.form_fields?.enabled}
            <div
              class="verbaflo-privacy-policy-container-wrapper"
              style="bottom: {privacyPolicyBottom} !important;"
            >
              <div class="verbaflo-privacy-policy-container">
                <div class="verbaflo-privacy-policy-container-content">
                  <div class="verbaflo-privacy-policy-header">
                    <span style="font-size: 1.5em;">👋</span>
                    <span>Confirm to continue</span>
                  </div>
                  <div class="verbaflo-privacy-policy-description">
                    {@html sanitizeHtml($client_config?.consent_message ?? "")}
                  </div>
                </div>
                <button
                  type="button"
                  class="verbaflo-privacy-policy-accept-button"
                  style="--accent-color: {accentColor} !important;"
                  on:click={handleStartChatting}
                >
                  I Agree
                </button>
              </div>
            </div>
          {/if}

          <div>
            {#if $selected_communication === "email"}
              <button
                type="button"
                on:click={(e) => handleCommunicationClick("email")}
                style="--accent-color: {accentColor};"
                class="verbaflo-communication-detail-container"
              >
                <EmailIcon />
                <p class="verbaflo-communication-detail-container-text">
                  {$client_config?.modality_config?.mail?.cta || "Send Email"}
                </p>
              </button>
            {/if}
            {#if $selected_communication === "phone"}
              <button
                type="button"
                on:click={(e) => handleCommunicationClick("call")}
                style="--accent-color: {accentColor};"
                class="verbaflo-communication-detail-container"
              >
                <PhoneIcon />
                <p class="verbaflo-communication-detail-container-text">
                  {$client_config?.modality_config?.call?.cta || "Call Us"}
                </p>
              </button>
            {/if}
            {#if $selected_communication === "whatsapp"}
              <button
                on:click={(e) => handleCommunicationClick("whatsapp")}
                type="button"
                style="--accent-color: {accentColor};"
                class="verbaflo-communication-detail-container"
              >
                <WhatsappIcon />
                <p class="verbaflo-communication-detail-container-text">
                  {$client_config?.modality_config?.whatsapp?.cta ||
                    "Open Whatsapp"}
                </p>
              </button>
            {/if}
            {#if $selected_communication === "sms"}
              <button
                on:click={(e) => handleCommunicationClick("sms")}
                type="button"
                style="--accent-color: {accentColor};"
                class="verbaflo-communication-detail-container"
              >
                <SmsIcon />
                <p class="verbaflo-communication-detail-container-text">
                  {$client_config?.modality_config?.sms?.cta || "Send SMS"}
                </p>
              </button>
            {/if}
            {#if $selected_communication === "fb_messenger"}
              <button
                on:click={(e) => handleCommunicationClick("fb_messenger")}
                type="button"
                style="--accent-color: {accentColor};"
                class="verbaflo-communication-detail-container"
              >
                <FbIcon size="20px" />
                <p class="verbaflo-communication-detail-container-text">
                  {$client_config?.modality_config?.fb_messenger?.cta ||
                    "Open Facebook Messenger"}
                </p>
              </button>
            {/if}
            {#if $selected_communication === "instagram"}
              <button
                on:click={(e) => handleCommunicationClick("instagram")}
                type="button"
                style="--accent-color: {accentColor};"
                class="verbaflo-communication-detail-container"
              >
                <InstagramIcon size="20px" />
                <p class="verbaflo-communication-detail-container-text">
                  {$client_config?.modality_config?.instagram?.cta ||
                    "Open Instagram"}
                </p>
              </button>
            {/if}
          </div>
          <CommunicationModalities {accentColor} {modalityConfig} {id} />
          {#if $is_feedback_form_enabled}
            <FeedbackForm
              {accentColor}
              {feedback_form_config}
              onStartNewChat={startNewChat}
            />
          {/if}
        </div>
      </div>
    {/if}

    {#if $attachmentModalOpen}
      <AttachmentModal />
    {/if}
  </div>
{/if}

<style>
  :global(#vfwidget) {
    touch-action: auto !important;
    overscroll-behavior: auto !important;
    pointer-events: auto !important;
  }

  :global(#vfwidget .verbaflo-widget-chat-container) {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    padding: 20px 16px 16px 12px !important;
    background-color: white !important;
    overflow-y: auto !important;
    flex: 1 1 auto !important;
    scroll-behavior: auto !important;
    -webkit-overflow-scrolling: touch !important;
    overscroll-behavior: contain !important;
    position: relative !important;
    z-index: 1 !important;
    transform: translateZ(0) !important;
    -webkit-transform: translateZ(0) !important;
    isolation: isolate !important;
    touch-action: pan-y !important;
    contain: layout style paint !important;
  }

  :global(#vfwidget *) {
    touch-action: auto !important;
    pointer-events: auto !important;
  }

  :global(#vfwidget .verbaflo-widget-container) {
    position: fixed !important;
    bottom: 6px !important;
    right: var(--vf-h-offset, 16px) !important;
    z-index: 2147483647 !important;
  }

  :global(#vfwidget .verbaflo-widget-container.verbaflo-widget-position-left) {
    right: auto !important;
    left: var(--vf-h-offset, 16px) !important;
  }

  :global(#vfwidget .verbaflo-widget-parent-container) {
    position: absolute !important;
    right: 0 !important;
    width: 400px !important;
    height: calc(100vh - 112px) !important;
    max-height: 600px !important;
    background-color: transparent !important;
    border-radius: 24px !important;
    box-shadow:
      0 10px 15px -3px rgb(0 0 0 / 0.1),
      0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
    display: flex !important;
    flex-direction: column !important;
    font-size: 14px !important;
    transition: all 0.3s ease-in-out !important;
  }

  :global(
      #vfwidget .verbaflo-widget-position-left .verbaflo-widget-parent-container
    ) {
    right: auto !important;
    left: 0 !important;
  }

  :global(#vfwidget .verbaflo-widget-chat-container-wrapper) {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    height: calc(100% - 52px) !important;
    background-color: white !important;
    border-bottom-left-radius: 24px !important;
    border-bottom-right-radius: 24px !important;
    overflow: hidden !important;
    position: relative !important;
  }
  :global(#vfwidget .verbaflo-widget-chat-message-container) {
    display: flex !important;
    flex-direction: column !important;
    row-gap: 16px !important;
  }
  :global(#vfwidget .verbaflo-widget-message-wrapper) {
    display: flex !important;
    align-items: flex-start !important;
    gap: 8px !important;
    width: fit-content !important;
    max-width: 100% !important;
  }
  :global(#vfwidget .verbaflo-widget-wechat-inline) {
    margin: 8px 0 !important;
    width: 100% !important;
  }
  :global(#vfwidget .verbaflo-widget-profile-image) {
    width: 32px !important;
    height: 32px !important;
    border-radius: 50% !important;
    object-fit: cover !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
  }
  :global(#vfwidget .verbaflo-widget-message-bubble) {
    background-color: #f6f6f6 !important;
    border-radius: 8px !important;
    border-bottom-left-radius: 0 !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    padding: 10px !important;
    max-width: 280px !important;
    word-break: break-word !important;
  }
  :global(#vfwidget .verbaflo-widget-message-sender-name) {
    font-weight: 500 !important;
    color: #051133 !important;
    font-size: 14px !important;
    margin: 0 !important;
    padding: 0 !important;
    text-transform: none !important;
    line-height: normal !important;
    margin-bottom: 4px !important;
  }
  :global(#vfwidget .verbaflo-widget-message-content-container *) {
    color: black !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    margin: 0 !important;
    padding: 0 !important;
    text-transform: none !important;
    line-height: normal !important;
  }
  :global(#vfwidget .verbaflo-widget-message-content-container > p) {
    letter-spacing: normal !important;
  }
  :global(#vfwidget .verbaflo-communication-detail-container) {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 16px auto !important;
    column-gap: 6px !important;
    color: white !important;
    border: 1px solid var(--accent-color) !important;
    border-radius: 8px !important;
    padding: 10px 22px !important;
    background-color: var(--accent-color) !important;
    font-size: 14px !important;
    max-width: fit-content !important;
    box-shadow: 0 3px 12px rgba(5, 17, 51, 0.18) !important;
    cursor: pointer !important;
    opacity: 1 !important;
    text-transform: none !important;
    transition: none !important;
  }
  :global(#vfwidget .verbaflo-communication-detail-container:hover),
  :global(#vfwidget .verbaflo-communication-detail-container:active) {
    opacity: 1 !important;
    text-decoration: none !important;
  }
  :global(#vfwidget .verbaflo-communication-detail-container-text) {
    color: white !important;
    font-size: 14px !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  :global(#vfwidget .verbaflo-privacy-policy-container-wrapper) {
    background-color: white !important;
    position: absolute !important;
    display: flex !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    z-index: 100 !important;
    padding: 0 16px !important;
  }

  :global(#vfwidget .verbaflo-privacy-policy-container) {
    background-color: #f6f6f6 !important;
    border-radius: 8px !important;
    padding: 16px 12px !important;
    gap: 20px !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    margin-top: auto !important;
  }
  :global(#vfwidget .verbaflo-privacy-policy-container-content) {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 7px !important;
  }

  :global(#vfwidget .verbaflo-privacy-policy-header) {
    font-size: 16px !important;
    font-weight: 500 !important;
    color: #000000 !important;
    text-align: center !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    gap: 0.5em !important;
  }

  :global(#vfwidget .verbaflo-privacy-policy-description) {
    font-size: 14px !important;
    font-weight: 400 !important;
    color: #535862 !important;
  }

  :global(#vfwidget .verbaflo-privacy-policy-description a) {
    font-size: 14px !important;
    font-weight: 500 !important;
    color: #3538cd !important;
    text-decoration: underline !important;
    cursor: pointer !important;
  }

  :global(#vfwidget .verbaflo-privacy-policy-privacy-policy-link) {
    font-size: 14px !important;
    font-weight: 500 !important;
    color: #3538cd !important;
    text-decoration: underline !important;
    cursor: pointer !important;
    border: none !important;
    background-color: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }

  :global(#vfwidget .verbaflo-cookies-policy-link) {
    font-size: 14px !important;
    font-weight: 500 !important;
    color: #3538cd !important;
    text-decoration: underline !important;
    cursor: pointer !important;
    border: none !important;
    background-color: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }

  :global(#vfwidget .verbaflo-privacy-policy-accept-button) {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: #ffffff !important;
    background-color: var(--accent-color) !important;
    border-radius: 6px !important;
    padding: 8px 0px !important;
    width: 100% !important;
    border: none !important;
    cursor: pointer !important;
  }

  :global(#vfwidget .verbaflo-privacy-policy-hello-icon) {
    font-size: 32px !important;
  }

  @media only screen and (max-width: 767px) and (orientation: portrait),
    only screen and (max-height: 500px) and (orientation: landscape) {
    :global(#vfwidget .verbaflo-widget-parent-container) {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      height: 100% !important;
      width: 100% !important;
      max-height: 100% !important;
      border-radius: 0 !important;
      margin: 0 !important;
      background-color: white !important;
    }
    :global(#vfwidget .verbaflo-widget-chat-container-wrapper) {
      background-color: white !important;
      height: calc(100% - 52px) !important;
    }
  }
</style>
